import apiSlice from 'api/baseApiSlice';
import { DocumentsTags } from 'api/documents/documentsTags';
import { UsersTags } from 'api/users/usersAPISlice';
import {
  CompletionStatus,
  type CountryCode,
  CountryCodeEnum,
  type SetVettingRoleRequest,
  type StartCharityVettingRequest,
  type UnwantedFoodType,
  type UpdateCharityVettingRequest,
  type CharityVettingSubmissionResponse,
  type CharityVettingSubmitCharityDetailsRequest,
  type DonationPreferencesEnum,
  type VettingRequestData,
  type VettingResponse,
} from 'features/charity-vetting/types/charityVettingTypes';
import { clearCharityVettingStorage } from 'features/charity-vetting/utils/storageHandlers';

import {
  type DeleteVettingDocumentRequest,
  type UploadVettingDocumentRequest,
  type UploadVettingDocumentResponse,
} from './documentTypes';

const VETTING_BASE_URL = '/charity/auth/v1/vetting';

const CHARITY_VETTING_TAG = 'CharityVetting';

const charityVettingApiSlice = apiSlice
  .enhanceEndpoints({
    addTagTypes: [UsersTags.CurrentUser, CHARITY_VETTING_TAG, DocumentsTags.Documents],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getCharityVetting: build.query<VettingRequestData, void>({
        query: () => ({
          url: VETTING_BASE_URL,
          method: 'get',
        }),
        providesTags: [CHARITY_VETTING_TAG],
      }),
      startCharityVetting: build.mutation<VettingResponse, StartCharityVettingRequest>({
        query: (data: StartCharityVettingRequest) => ({
          url: `${VETTING_BASE_URL}/start`,
          method: 'post',
          data,
        }),
        // Creates the record, so the previously-404 vetting query must refetch.
        invalidatesTags: [UsersTags.CurrentUser, CHARITY_VETTING_TAG],
      }),
      setCharityVettingRole: build.mutation<VettingResponse, SetVettingRoleRequest>({
        query: (data: SetVettingRoleRequest) => ({
          url: `${VETTING_BASE_URL}/role`,
          method: 'post',
          data,
        }),
        invalidatesTags: [UsersTags.CurrentUser],
      }),
      updateCharityVetting: build.mutation<VettingResponse, UpdateCharityVettingRequest>({
        query: (data: UpdateCharityVettingRequest) => ({
          url: `${VETTING_BASE_URL}/update`,
          method: 'post',
          data,
        }),
        invalidatesTags: [UsersTags.CurrentUser],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (!data?.data) {
            return;
          }
          dispatch(
            charityVettingApiSlice.util.updateQueryData('getCharityVetting', undefined, (cache) => {
              Object.assign(cache, data.data);
            }),
          );
          // Clear the transient carrier once the wizard is actually finished.
          if (arg.completionStatus === CompletionStatus.SUBMITTED) {
            clearCharityVettingStorage();
          }
        },
      }),
      charityVettingApiSubmitCharityDetails: build.mutation<
        CharityVettingSubmissionResponse,
        CharityVettingSubmitCharityDetailsRequest
      >({
        query: (postData: CharityVettingSubmitCharityDetailsRequest) => ({
          url: VETTING_BASE_URL,
          method: 'post',
          data: postData,
        }),
        invalidatesTags: [UsersTags.CurrentUser],
        async onQueryStarted(_arg, { queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            clearCharityVettingStorage();
          }
        },
      }),
      uploadVettingDocument: build.mutation<
        UploadVettingDocumentResponse['data'],
        UploadVettingDocumentRequest
      >({
        query: ({ file, documentType, mandatory }) => {
          const data = new FormData();
          data.append('file', file);
          data.append('documentType', documentType);
          data.append('mandatory', String(mandatory));
          return { url: `${VETTING_BASE_URL}/documents/upload`, method: 'post', data };
        },
        transformResponse: (response: UploadVettingDocumentResponse) => response.data,
        invalidatesTags: [DocumentsTags.Documents],
      }),
      deleteVettingDocument: build.mutation<void, DeleteVettingDocumentRequest>({
        query: (data) => ({
          url: `${VETTING_BASE_URL}/documents/delete`,
          method: 'post',
          data,
        }),
        invalidatesTags: [DocumentsTags.Documents],
      }),
      charityVettingApiGetCharityItemCategories: build.query<
        UnwantedFoodType[],
        {
          countryIsoCode: CountryCode;
          donationPreferences?: DonationPreferencesEnum;
        }
      >({
        query: ({
          countryIsoCode,
          donationPreferences,
        }: {
          countryIsoCode: CountryCode;
          donationPreferences?: DonationPreferencesEnum;
        }) => ({
          url: '/charity/donations/v1/item-categories',
          method: 'post',
          data: {
            countryIsoCode: countryIsoCode ?? CountryCodeEnum.GB,
            itemCategoryType: donationPreferences,
          },
        }),
      }),
    }),
  });

export default charityVettingApiSlice;
